export interface BlockLine {
  id: string;
  blocks: number; // Number of blocks in this line
  color: string;
  label?: string; // Optional label for the line
}

export interface BlockGroup {
  id: string;
  lines: BlockLine[];
  arrangement: 'parallel' | 'sequence';
}

/**
 * Generates a unique, vibrant color for a given index.
 * Uses the golden angle to distribute hues evenly around the color wheel,
 * with subtle variations in saturation and lightness for additional uniqueness.
 * Starts with blue to avoid semantically loaded colors like red and green.
 */
export function generateBlockColor(index: number): string {
  // Golden angle in degrees (~137.5°) - provides optimal distribution of colors
  const goldenAngle = 137.508;

  // Start at blue (210°) to avoid red/green which are semantically loaded
  const hueOffset = 210;

  // Calculate hue using golden angle distribution
  // This ensures each color is well-separated from the previous ones
  const hue = (index * goldenAngle + hueOffset) % 360;

  // Vary saturation slightly (70-95%) to add variety while keeping colors vibrant
  const saturationBase = 70 + (index % 6) * 4;
  const saturation = saturationBase + ((index * 7) % 11); // 70-95%

  // Vary lightness slightly (45-60%) for good contrast and readability
  const lightnessBase = 45 + (index % 4) * 4;
  const lightness = lightnessBase + ((index * 3) % 7); // 45-60%

  return `hsl(${hue.toFixed(1)}, ${saturation}%, ${lightness}%)`;
}
